import React from "react";
import { Sparkles, ChevronRight, Check } from "lucide-react";
import { Frame, Pill } from "@/components/features/FeatureKit";

/* Accurate, non-interactive representations of real Startup Otter screens, built with the same
   design tokens the product uses. They render no live data and trigger no requests. */

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-[560] text-muted-foreground">{children}</span>
);

const GhostAction = ({ icon: Icon, children, solid = false }) => (
  <span className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-[560] ${solid ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
    <Icon className="h-4 w-4" aria-hidden="true" /> {children}
  </span>
);

/** Community Needs card — mirrors the delivered-need card, including "Why you matched". */
export const NeedCardMock = ({ testId = "mock-need-card" }) => (
  <Frame url="startupotter.com/dashboard/otter-assist" testId={testId}>
    <div className="space-y-4 bg-background p-5">
      <div className="flex items-center gap-2 text-sm font-[640] text-foreground">
        <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" /> Community Needs
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-full bg-primary/15" />
          <span className="text-sm font-[640] text-foreground">Grace Kim</span>
          <Chip>PERSONAL</Chip>
          <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-[700] uppercase text-primary-foreground">New</span>
        </div>
        <p className="mt-3 text-sm leading-6 text-foreground">
          Looking for a fractional finance operator who has run climate hardware runway planning. We have a
          board review coming up and need help modelling scenarios.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 font-[600] text-primary"><Sparkles className="h-3 w-3" aria-hidden="true" /> Otter Assist</span>
          <span>Soon — within 7 days</span>
        </div>
        <div className="mt-3 rounded-xl bg-[hsl(45_90%_95%)] p-3 ring-1 ring-[hsl(45_80%_80%)]">
          <div className="text-[10px] font-[700] uppercase tracking-[0.1em] text-[hsl(35_60%_32%)]">Why you matched</div>
          <p className="mt-1 text-xs leading-5 text-[hsl(35_35%_28%)]">
            You have recent public activity on this topic, you work in a closely related problem area and
            your working style is compatible.
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <GhostAction icon={Check} solid>I Can Help</GhostAction>
          <GhostAction icon={ChevronRight}>View Need</GhostAction>
        </div>
      </div>
    </div>
  </Frame>
);

/** Progressive outreach: 5 → 10 → 15. */
export const WavesMock = ({ testId = "mock-waves" }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={testId}>
    <div className="grid grid-cols-3 gap-3">
      {[
        { n: 5, label: "Wave 1" },
        { n: 10, label: "Wave 2" },
        { n: 15, label: "Wave 3" },
      ].map((w, i) => (
        <div key={w.label} className={`rounded-xl p-4 text-center ${i === 0 ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
          <div className="text-3xl font-[700]">{w.n}</div>
          <div className={`mt-1 text-xs font-[560] ${i === 0 ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{w.label}</div>
        </div>
      ))}
    </div>
    <p className="mt-4 text-xs leading-5 text-muted-foreground">
      Outreach opens with five people and only widens if the need still calls for it — up to 30 members in total.
    </p>
  </div>
);

/** Refinement chips: Industry / Region / Work Setting with Preferred vs Required. */
export const RefineMock = ({ testId = "mock-refine" }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={testId}>
    {[
      { label: "Industry", value: "Climate Hardware", mode: "Required" },
      { label: "Region", value: "Pacific Northwest", mode: "Preferred" },
      { label: "Work setting", value: "Remote friendly", mode: "Preferred" },
    ].map((r) => (
      <div key={r.label} className="flex flex-wrap items-center justify-between gap-3 border-b border-border py-3 last:border-0">
        <div>
          <div className="text-[11px] font-[600] uppercase tracking-[0.1em] text-muted-foreground">{r.label}</div>
          <div className="text-sm font-[600] text-foreground">{r.value}</div>
        </div>
        <Pill active={r.mode === "Required"}>{r.mode}</Pill>
      </div>
    ))}
  </div>
);

export const UrgencyMock = ({ testId = "mock-urgency" }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={testId}>
    <div className="flex flex-wrap gap-2">
      {["Immediate — Today", "ASAP", "Soon", "Within X days", "Flexible"].map((u, i) => (
        <Pill key={u} active={i === 2}>{u}</Pill>
      ))}
    </div>
    <p className="mt-4 text-xs leading-5 text-muted-foreground">Timing travels with the need, so helpers know what they are stepping into.</p>
  </div>
);

export const AudienceMock = ({ testId = "mock-audience" }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={testId}>
    {[
      { t: "My Network", d: "People you are already friends with." },
      { t: "Network + Extended", d: "One step further out into the community." },
      { t: "Eligible Community", d: "The wider Startup Otter community that opted in." },
    ].map((s, i) => (
      <div key={s.t} className={`mt-3 rounded-xl p-4 first:mt-0 ${i === 2 ? "bg-primary/10 ring-1 ring-primary/25" : "bg-secondary"}`}>
        <div className="text-sm font-[640] text-foreground">{s.t}</div>
        <div className="mt-1 text-xs leading-5 text-muted-foreground">{s.d}</div>
      </div>
    ))}
  </div>
);

export const PrefsMock = ({ testId = "mock-prefs" }) => (
  <Frame url="startupotter.com/dashboard/otter-assist" testId={testId}>
    <div className="space-y-3 bg-background p-5">
      <div className="text-sm font-[680] text-foreground">Otter Assist preferences</div>
      {[
        ["Receive Otter Assist matches", "On"],
        ["Who can reach you", "Network + Extended + Community"],
        ["Daily Community Needs limit", "15 a day"],
        ["Email digest", "5:00 PM, your time zone"],
        ["Pause Otter Assist", "Not paused"],
      ].map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-3 rounded-xl bg-card px-4 py-3 ring-1 ring-border">
          <span className="text-sm text-foreground">{k}</span>
          <span className="text-xs font-[600] text-primary">{v}</span>
        </div>
      ))}
    </div>
  </Frame>
);

/** Analytics KPI row. */
export const KpiMock = ({ items, testId = "mock-kpi" }) => (
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid={testId}>
    {items.map((k) => (
      <div key={k.label} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[10px] font-[700] uppercase tracking-[0.1em] text-muted-foreground">{k.label}</span>
          {k.delta ? <span className="text-[11px] font-[640] text-primary">{k.delta}</span> : null}
        </div>
        <div className="mt-2 text-2xl font-[700] text-foreground">{k.value}</div>
      </div>
    ))}
  </div>
);

export const FunnelMock = ({ testId = "mock-funnel" }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={testId}>
    <div className="text-sm font-[680] text-foreground">Conversion funnel</div>
    <p className="mt-1 text-xs text-muted-foreground">Server-provided stages only.</p>
    <div className="mt-4 space-y-3">
      {[["Visits", 100], ["Unique visitors", 74], ["Engaged", 38], ["Converted", 12]].map(([label, pct]) => (
        <div key={label}>
          <div className="flex items-center justify-between text-sm text-foreground"><span>{label}</span></div>
          <div className="mt-1 h-2 w-full rounded-full bg-secondary">
            <div className="h-2 rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const RankingsMock = ({ testId = "mock-rankings" }) => (
  <Frame url="startupotter.com/dashboard/analytics" testId={testId}>
    <div className="bg-background p-5">
      <div className="text-sm font-[680] text-foreground">Your entities</div>
      <p className="mt-1 text-xs text-muted-foreground">Ranked across everything you manage.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {["Most viewed", "Fastest growing", "Most engaged", "Most converted"].map((t, i) => (
          <Pill key={t} active={i === 0}>{t}</Pill>
        ))}
      </div>
      <div className="mt-4 divide-y divide-border rounded-xl bg-card ring-1 ring-border">
        {[
          ["1", "Anchor Legal Partners", "Business"],
          ["2", "Pre-Seed Pitch Night", "Event"],
          ["3", "Angel Investing 101", "Resource"],
          ["4", "Launchpad Accelerator", "Capital"],
        ].map(([rank, name, kind]) => (
          <div key={rank} className="flex items-center gap-3 px-4 py-3">
            <span className="text-xs text-muted-foreground">{rank}</span>
            <span className="flex-1 truncate text-sm font-[600] text-foreground">{name}</span>
            <span className="text-xs text-muted-foreground">{kind}</span>
          </div>
        ))}
      </div>
    </div>
  </Frame>
);

export const TeamRolesMock = ({ testId = "mock-team" }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={testId}>
    {[
      ["Owner", "Full control of the organization."],
      ["Admin", "Manage the profile and the team."],
      ["Contributor", "Help with the parts they are given."],
    ].map(([role, desc]) => (
      <div key={role} className="flex items-start gap-3 border-b border-border py-3 last:border-0">
        <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
        <div>
          <div className="text-sm font-[640] text-foreground">{role}</div>
          <div className="text-xs leading-5 text-muted-foreground">{desc}</div>
        </div>
      </div>
    ))}
  </div>
);

export const ProfileFieldsMock = ({ items, title = "Your profile", testId = "mock-profile-fields" }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={testId}>
    <div className="text-sm font-[680] text-foreground">{title}</div>
    <div className="mt-4 space-y-3">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-xl bg-secondary/60 px-4 py-3">
          <div className="text-[10px] font-[700] uppercase tracking-[0.1em] text-muted-foreground">{label}</div>
          <div className="mt-1 text-sm text-foreground">{value}</div>
        </div>
      ))}
    </div>
  </div>
);
